import { useMemo } from 'react';
import { NodePicker } from './NodePicker';
import {
  NODE_PICKER_WIDTH,
  NODE_PICKER_HEIGHT,
  PLUS_BUTTON_RADIUS,
  NODE_PICKER_GAP,
} from '../styles';
import { NodeType } from './registry';

interface NodePickerOverlayProps {
  /** The ID of the parent node */
  parentNodeId: string;

  /** The bounding rect of the plus button (screen coordinates) */
  buttonRect: DOMRect;

  /** Callback when picker should close */
  onClose: () => void;

  /** Callback when user selects a node type */
  onSelect: (nodeType: NodeType) => void;
}

/**
 * Renders NodePicker with correct screen positioning based on button location.
 * Must be rendered inside ReactFlow to receive click-outside events correctly.
 */
export function NodePickerOverlay({
  parentNodeId,
  buttonRect,
  onClose,
  onSelect,
}: NodePickerOverlayProps) {
  const screenPosition = useMemo(() => {
    // Button center in screen coordinates
    const buttonCenterX = buttonRect.left + buttonRect.width / 2;
    const buttonCenterY = buttonRect.top + buttonRect.height / 2;

    // Position picker with its top near the button center
    let finalY = buttonCenterY - 20;

    // Ensure not off top or bottom of screen
    finalY = Math.max(10, finalY);
    finalY = Math.min(window.innerHeight - NODE_PICKER_HEIGHT - 10, finalY);

    // Position picker to the right of the button by default
    const rightX = buttonCenterX + PLUS_BUTTON_RADIUS + NODE_PICKER_GAP;
    const wouldOverflowRight = rightX + NODE_PICKER_WIDTH > window.innerWidth;

    let finalX: number;
    let alignRight: boolean;

    if (wouldOverflowRight) {
      // Position to the left of the button
      finalX = buttonCenterX - PLUS_BUTTON_RADIUS - NODE_PICKER_GAP;
      alignRight = true;
    } else {
      // Position to the right of the button
      finalX = rightX;
      alignRight = false;
    }

    return { x: finalX, y: finalY, alignRight };
  }, [buttonRect]);

  return (
    <NodePicker
      parentNodeId={parentNodeId}
      open={true}
      onClose={onClose}
      onSelect={onSelect}
      anchorPosition={screenPosition}
    />
  );
}
